import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useColumnOrderSave } from "../../../core/common/useColumnOrderSave";
import CollapseHeader from "../../../core/common/collapse-header";
import Table from "../../../core/common/dataTable/index";
import { RootState } from "../../../core/data/redux/store";
import { useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import {
  fetchAllLeadSources,
  updateLeadSource,
  deleteLeadSource,
  LeadSource,
  createLeadSource,
  exportLeadSourcePDF,
  exportLeadSourceExcel,
  exportSelectedLeadSourcePDF,
  exportSelectedLeadSourceExcel,
  deleteLeadSourceBulk,
  importLeadSourceExcel,
  LeadSourceImportResult,
} from "../../../api/leadsource";
import { toast } from "react-toastify";
import { highlightMatch } from "../../../core/common/highlightmatch";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { initialSettings } from "../../../core/common/selectoption/selectoption";
import { Dropdown, Menu, Button, Checkbox, Pagination } from "antd";
import { DownOutlined, FunnelPlotFilled } from "@ant-design/icons";
import CountUp from "react-countup";
import {
  validateLeadsource,
  validateLeadStatus,
  ValidationResult,
  validateNoDoubleSpacesLead,
  validateNotOnlySymbolsLead,
  validateNoRepeatedCharactersLead,
  validateNoEdgeSymbols,
  validateNotOnlyNumbersLead,
  validateLeadSourceDuplicate,
} from "../../../api/leadValidation";
import Modal from "bootstrap/js/dist/modal";
import AnimatedBadge from "../../../core/common/AnimatedBadge/index";
import KpiCard from "../../../core/common/KPIcard";
import Tip from "../../../core/common/tooltip";
import ColumnManagement from "../../../core/common/ColumnManagement";
import SubmoduleHoverCard2 from "../../../core/common/SubCard2Button";
import ScrollToTop from "../../../core/common/ScrollToTop";
import ModalDeleteEffect from "../../../core/common/ModalDeleteEffect";
import GridView from "../../../core/common/GridView";

const LeadsSource = () => {
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<LeadSource[]>([]);
  const [orderedData, setOrderedData] = useState<LeadSource[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]); // For selected LeadSource_id;
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false); // For Add & Edit loading
  const [editLeadSource, setEditLeadSource] = useState<LeadSource | null>(null); // ✅ Selected data for Edit
  const [deleteLeadSourceId, setDeleteLeadSourceId] = useState<string | null>(
    null
  ); // ✅ Selected ID for Delete
  const route = all_routes;
  const userRole = useSelector((state: RootState) => state.auth.role);

  const [newName, setNewName] = useState<string>("");
  const [newStatus, setNewStatus] = useState<string>("Active");
  const [editName, setEditName] = useState<string>("");
  const [editStatus, setEditStatus] = useState<string>("Active");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [addNameError, setAddNameError] = useState<string>("");
  const [addStatusError, setAddStatusError] = useState<string>("");

  const [editNameError, setEditNameError] = useState<string>("");
  const [editStatusError, setEditStatusError] = useState<string>("");
  const runValidation = (
    result: ValidationResult,
    setter: (msg: string) => void
  ): boolean => {
    setter(result.valid ? "" : result.message);
    return result.valid;
  };

  // Full validation for adding a lead source
  const validateAddLeadSourceAll = (value: string) => {
    let res = validateLeadsource(value);
    if (!res.valid) return res;

    res = validateNoDoubleSpacesLead(value);
    if (!res.valid) return res;

    res = validateNotOnlySymbolsLead(value);
    if (!res.valid) return res;

    res = validateNoRepeatedCharactersLead(value);
    if (!res.valid) return res;

    res = validateNoEdgeSymbols(value, "Lead Source");
    if (!res.valid) return res;

    res = validateNotOnlyNumbersLead(value);
    if (!res.valid) return res;

    res = validateLeadSourceDuplicate(
      value,
      dataSource.map((x) => x.LeadSource_name)
    );
    if (!res.valid) return res;

    return { valid: true, message: "" };
  };

  const [selectedDateRange, setSelectedDateRange] = useState<
    [Date, Date] | null
  >(null);
  const [activeRangeLabel, setActiveRangeLabel] = useState("Last 30 Days");

  const [sortOption, setSortOption] = useState<string>("recently_added");

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    {
      index: true,
      LeadSource_name: true,
      created_at: true,
      Lead_status: true,
      action: true,
    }
  );
  const [statusFilter, setStatusFilter] = useState("all");
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterNamesSearch, setFilterNamesSearch] = useState("");
  const [filterStatusesSearch, setFilterStatusesSearch] = useState("");
  const [filterCreatedAts, setFilterCreatedAts] = useState<string[]>([]);
  const [createdAtSearch, setCreatedAtSearch] = useState("");
  const [alphaFilter, setAlphaFilter] = useState<string>("All");
  const [isCreateValid, setIsCreateValid] = useState(false);
  const [isEditValid, setIsEditValid] = useState(false);
  const defaultColumnOrder = [
    "index",
    "LeadSource_name",
    "created_at",
    "Lead_status",
    "action",
  ];
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] =
    useState<LeadSourceImportResult | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Column order state for drag and drop (only non-sticky columns)
  const [columnKeys, setColumnKeys] = useState<string[]>(() => {
    const saved = localStorage.getItem("leadsourceColumnKeys");
    if (saved) return JSON.parse(saved);
    return defaultColumnOrder;
  });

  // Hook for save prompt on navigation/refresh
  const { SaveModal } = useColumnOrderSave({
    columnKeys,
    localStorageKey: "leadsourceColumnKeys",
    defaultOrder: defaultColumnOrder,
  });

  // Accordion toggle
  const handleAccordionToggle = (key: string) => {
    setOpenAccordion((prev) => (prev === key ? null : key));
  };

  // Checkbox selection toggle
  const handleFilterChange = (
    value: string,
    current: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (current.includes(value)) {
      setter(current.filter((v) => v !== value));
    } else {
      setter([...current, value]);
    }
  };

  // ✅ Unique lists with useMemo (like your Lead Industry example)
  const uniqueNames = useMemo(
    () =>
      Array.from(
        new Set(dataSource.map((d) => d.LeadSource_name?.trim() || ""))
      ).filter((n) => n),
    [dataSource]
  );

  const uniqueStatuses = useMemo(
    () =>
      Array.from(
        new Set(dataSource.map((d) => d.Lead_status?.trim() || ""))
      ).filter((s) => s),
    [dataSource]
  );

  // ✅ Unique Created At values formatted as dd/mm/yyyy
  const uniqueCreatedAts = useMemo(() => {
    const formattedDates = dataSource
      .map((d) => {
        if (!d.created_at) return "";
        const date = new Date(d.created_at);
        if (isNaN(date.getTime())) return "";
        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      })
      .filter((n) => n);

    return Array.from(new Set(formattedDates));
  }, [dataSource]);

  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  // Fetch Lead Sources
  const loadLeadSources = async () => {
    setLoading(true);
    setError(null); // Reset previous error

    const data = await fetchAllLeadSources(); // toast already shows error
    setDataSource(data);
    setOrderedData(data);

    setLoading(false);
  };

  const handleCreateLeadSource = async () => {
    const nameRes = validateLeadsource(newName);
    const statusRes = validateLeadStatus(newStatus);

    const nameOk = runValidation(nameRes, setAddNameError);
    const statusOk = runValidation(statusRes, setAddStatusError);

    if (!nameOk || !statusOk) {
      toast.error("Please fix the errors above.");
      return;
    }

    setSaving(true); // ← Start loading

    try {
      const newLeadSource = {
        LeadSource_name: newName.trim(),
        Lead_status: newStatus,
      };

      const created = await createLeadSource(newLeadSource);
      if (created) {
        //toast.success("Lead Source created successfully!");
        await loadLeadSources();
        setNewName("");
        setNewStatus("Active");
        setAddNameError("");
        setAddStatusError("");

        // Close modal
        const modalElement = document.getElementById("add_calls");
        if (modalElement) {
          const modalInstance =
            Modal.getInstance(modalElement) || new Modal(modalElement);
          modalInstance.hide();
        }
      }
    } catch (err) {
      // Error already handled by API toast
    } finally {
      setSaving(false); // ← Always stop loading
    }
  };

  useEffect(() => {
    const styleId = "animated-badge-keyframes";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.innerText = `
        @keyframes shine {
          0% {
            transform: skewX(-20deg) translateX(-150%);
          }
          50% {
             transform: skewX(-20deg) translateX(150%);
          }
          100% {
            transform: skewX(-20deg) translateX(150%);
          }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  useEffect(() => {
    loadLeadSources();
  }, []);

  // Make modals draggable
  useEffect(() => {
    const makeModalDraggable = (modalId: string) => {
      const modalElement = document.getElementById(modalId);
      if (!modalElement) return;

      const modalDialog = modalElement.querySelector(
        ".modal-dialog"
      ) as HTMLElement;
      const modalHeader = modalElement.querySelector(
        ".modal-header"
      ) as HTMLElement;

      if (!modalDialog || !modalHeader) return;

      let isDragging = false;
      let currentX = 0;
      let currentY = 0;
      let initialX = 0;
      let initialY = 0;
      let startX = 0;
      let startY = 0;

      // Reset position when modal is shown
      const resetPosition = () => {
        // Reset all position variables
        currentX = 0;
        currentY = 0;
        initialX = 0;
        initialY = 0;
        startX = 0;
        startY = 0;
        isDragging = false;

        // Remove only the inline styles that were added during dragging
        modalDialog.style.position = "";
        modalDialog.style.top = "";
        modalDialog.style.left = "";
        modalDialog.style.transform = "";
        modalDialog.style.margin = "";
        modalDialog.style.transition = "";
        modalDialog.style.width = "";
        modalDialog.style.maxWidth = "";

        // Restore centered class if it was removed
        if (!modalDialog.classList.contains("modal-dialog-centered")) {
          modalDialog.classList.add("modal-dialog-centered");
        }
      };

      // Listen for modal show event - reset when modal is about to show
      const handleShow = () => {
        // Reset immediately when modal starts showing
        resetPosition();
      };

      // Listen for modal shown event - ensure reset after modal is fully displayed
      const handleShown = () => {
        // Double-check reset after modal is fully shown
        resetPosition();
      };

      // Listen for modal hidden event - reset when modal is fully hidden
      const handleHidden = () => {
        resetPosition();
      };

      modalElement.addEventListener("show.bs.modal", handleShow);
      modalElement.addEventListener("shown.bs.modal", handleShown);
      modalElement.addEventListener("hidden.bs.modal", handleHidden);

      const dragStart = (e: MouseEvent) => {
        // Only allow dragging from header, not from close button or other interactive elements
        const target = e.target as HTMLElement;
        if (
          target.closest(".btn-close") ||
          target.closest("button") ||
          target.closest("input") ||
          target.closest("select")
        ) {
          return;
        }

        if (
          e.target === modalHeader ||
          modalHeader.contains(e.target as Node)
        ) {
          isDragging = true;
          modalHeader.style.cursor = "move";

          // Get current position and size of modal dialog
          const rect = modalDialog.getBoundingClientRect();
          startX = rect.left;
          startY = rect.top;

          // Get computed styles to preserve the modal's size
          const computedStyle = getComputedStyle(modalDialog);
          const currentWidth = computedStyle.width;
          const currentMaxWidth = computedStyle.maxWidth;
          const currentMinWidth = computedStyle.minWidth;

          // Remove centered class to allow free movement
          modalDialog.classList.remove("modal-dialog-centered");

          // Set initial position based on current position
          modalDialog.style.position = "fixed";
          modalDialog.style.top = `${startY}px`;
          modalDialog.style.left = `${startX}px`;
          modalDialog.style.margin = "0";
          modalDialog.style.transform = "none";
          modalDialog.style.transition = "none";

          // Preserve the modal's width and max-width to prevent size change
          if (
            currentWidth &&
            currentWidth !== "auto" &&
            currentWidth !== "0px"
          ) {
            modalDialog.style.width = currentWidth;
          }
          if (
            currentMaxWidth &&
            currentMaxWidth !== "none" &&
            currentMaxWidth !== "0px"
          ) {
            modalDialog.style.maxWidth = currentMaxWidth;
          }
          if (
            currentMinWidth &&
            currentMinWidth !== "0px" &&
            currentMinWidth !== "auto"
          ) {
            modalDialog.style.minWidth = currentMinWidth;
          }

          initialX = e.clientX - startX;
          initialY = e.clientY - startY;
        }
      };

      const drag = (e: MouseEvent) => {
        if (!isDragging) return;

        e.preventDefault();

        // Calculate new position
        const newX = e.clientX - initialX;
        const newY = e.clientY - initialY;

        // Get modal dialog dimensions
        const rect = modalDialog.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        const maxY = window.innerHeight - rect.height;

        // Constrain to viewport
        const constrainedX = Math.max(0, Math.min(newX, maxX));
        const constrainedY = Math.max(0, Math.min(newY, maxY));

        // Update position
        modalDialog.style.left = `${constrainedX}px`;
        modalDialog.style.top = `${constrainedY}px`;

        // Store current position for next drag
        currentX = constrainedX;
        currentY = constrainedY;
      };

      const dragEnd = () => {
        if (isDragging) {
          isDragging = false;
          modalHeader.style.cursor = "move";
          modalDialog.style.transition = "";
          // Keep the fixed positioning during drag, but allow reset on next open
        }
      };

      // Add cursor style to header
      modalHeader.style.cursor = "move";
      modalHeader.style.userSelect = "none";

      // Event listeners
      modalHeader.addEventListener("mousedown", dragStart);
      document.addEventListener("mousemove", drag);
      document.addEventListener("mouseup", dragEnd);

      // Cleanup function
      return () => {
        modalElement.removeEventListener("show.bs.modal", handleShow);
        modalElement.removeEventListener("shown.bs.modal", handleShown);
        modalElement.removeEventListener("hidden.bs.modal", handleHidden);
        modalHeader.removeEventListener("mousedown", dragStart);
        document.removeEventListener("mousemove", drag);
        document.removeEventListener("mouseup", dragEnd);
      };
    };

    // Make all three modals draggable
    const cleanup1 = makeModalDraggable("add_calls");
    const cleanup2 = makeModalDraggable("edit_calls");
    const cleanup3 = makeModalDraggable("delete_calls");
    const cleanup4 = makeModalDraggable("bulk_delete_modal");
    const cleanup5 = makeModalDraggable("import_excel_modal");

    return () => {
      cleanup1?.();
      cleanup2?.();
      cleanup3?.();
      cleanup4?.();
      cleanup5?.();
    };
  }, []);

  const handleEditClick = (record: LeadSource) => {
    setEditLeadSource(record);
    setEditName(record.LeadSource_name);
    setEditStatus(record.Lead_status);

    // 🔥 Run validation immediately when modal opens
    const nameRes = validateLeadsource(record.LeadSource_name || "");
    const statusRes = validateLeadStatus(record.Lead_status || "");

    const nameOk = runValidation(nameRes, setEditNameError);
    const statusOk = runValidation(statusRes, setEditStatusError);

    setIsEditValid(nameOk && statusOk);
  };

  const handleSaveEdit = async () => {
    if (!editLeadSource?.LeadSource_id) return;

    const nameRes = validateLeadsource(editName);
    const statusRes = validateLeadStatus(editStatus);

    const nameOk = runValidation(nameRes, setEditNameError);
    const statusOk = runValidation(statusRes, setEditStatusError);

    if (!nameOk || !statusOk) {
      toast.error("Please fix the errors.");
      return;
    }

    setSaving(true); // ← Start loading

    try {
      const success = await updateLeadSource(editLeadSource.LeadSource_id, {
        LeadSource_name: editName.trim(),
        Lead_status: editStatus,
      });

      if (success) {
        await loadLeadSources();
        setEditNameError("");
        setEditStatusError("");

        const modalElement = document.getElementById("edit_calls");
        if (modalElement) {
          const modalInstance =
            Modal.getInstance(modalElement) || new Modal(modalElement);
          modalInstance.hide();
        }
      }
    } catch (err) {
      // Error handled in API
    } finally {
      setSaving(false); // ← Stop loading
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteLeadSourceId) return;

    setDeleting(true); // ← Start loading
    try {
      const success = await deleteLeadSource(deleteLeadSourceId);
      if (success) {
        await loadLeadSources();
      }
    } catch (err) {
      // Error already handled in API
    } finally {
      setDeleting(false); // ← Always stop loading
    }
  };

  const totalCount = useMemo(() => dataSource.length, [dataSource]);

  const activeCount = useMemo(
    () =>
      dataSource.filter((item) => item.Lead_status?.toLowerCase() === "active")
        .length,
    [dataSource]
  );

  const inactiveCount = useMemo(
    () =>
      dataSource.filter(
        (item) => item.Lead_status?.toLowerCase() === "inactive"
      ).length,
    [dataSource]
  );

  const thisMonthCount = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    return dataSource.filter((item) => {
      if (!item.created_at) return false;
      const date = new Date(item.created_at);
      return (
        date.getMonth() === currentMonth && date.getFullYear() === currentYear
      );
    }).length;
  }, [dataSource]);

  const filteredData = useMemo(() => {
    const q = searchText.toLowerCase();
    const filtered = dataSource.filter((item) => {
      const name = item.LeadSource_name?.toLowerCase() || "";
      const status = item.Lead_status?.toLowerCase() || "";
      const created = item.created_at
        ? new Date(item.created_at).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        : "";

      const alphaMatch =
        alphaFilter === "All" ||
        (item.LeadSource_name || "")
          .toLowerCase()
          .startsWith(alphaFilter.toLowerCase());

      const matchName =
        filterNames.length === 0 ||
        filterNames.includes(item.LeadSource_name || "");

      const matchStatus =
        filterStatuses.length === 0 ||
        filterStatuses.includes(item.Lead_status || "");

      const matchCreated =
        filterCreatedAts.length === 0 || filterCreatedAts.includes(created);
      const matchesSearch =
        name.includes(q) || status.includes(q) || created.includes(q);

      // Filter by selected date range
      let matchesDate = true;
      if (selectedDateRange && created) {
        const createdDate = new Date(created);
        const [start, end] = selectedDateRange;
        matchesDate = createdDate >= start && createdDate <= end;
      }

      if (statusFilter !== "all" && status !== statusFilter) {
        return false;
      }

      return (
        matchesSearch &&
        matchesDate &&
        matchName &&
        matchStatus &&
        matchCreated &&
        alphaMatch
      );
    });

    // Sorting logic remains the same
    const sorted = [...filtered];
    if (sortOption === "ascending") {
      sorted.sort((a, b) =>
        (a.LeadSource_name || "").localeCompare(b.LeadSource_name || "")
      );
    } else if (sortOption === "descending") {
      sorted.sort((a, b) =>
        (b.LeadSource_name || "").localeCompare(a.LeadSource_name || "")
      );
    } else if (sortOption === "recently_added") {
      sorted.sort(
        (a, b) =>
          new Date(b.created_at || "").getTime() -
          new Date(a.created_at || "").getTime()
      );
    } else if (sortOption === "recently_viewed") {
      sorted.sort((a, b) => {
        const aTime = new Date(
          (a as any).last_viewed || a.created_at || ""
        ).getTime();
        const bTime = new Date(
          (b as any).last_viewed || b.created_at || ""
        ).getTime();
        return bTime - aTime;
      });
    }

    return sorted;
  }, [
    searchText,
    dataSource,
    sortOption,
    selectedDateRange,
    statusFilter,
    filterNames,
    filterStatuses,
    filterCreatedAts,
    alphaFilter,
  ]);

  const columns = useMemo(
    () => [
      {
        title: "S.No",
        key: "index",
        width: 80,
        align: "center" as const,
        render: (_: any, __: any, index: number) => (
          <span className="text-muted fw-medium">
            {(currentPage - 1) * pageSize + index + 1}
          </span>
        ),
      },

      {
        title: (
          <div className="d-flex align-items-center justify-content-between"
            onClick={(e) => e.stopPropagation()}>
            <span className="fw-semibold text-dark">Lead Source Name</span>

            {/* Alphabetical Filter - Clean & Professional */}
            <Dropdown
              trigger={["click"]}
              onOpenChange={(open) =>
                open && setTimeout(() => document.body.click(), 0)
              } // optional: close on outside click
              overlay={
                <Menu
                  selectedKeys={alphaFilter ? [alphaFilter] : ["All"]}
                  onClick={({ key }) =>
                    setAlphaFilter(key === "All" ? "" : key)
                  }
                  style={{ width: 50, maxHeight: 250, overflow: "auto" }}
                >
                  <Menu.Item key="All" className="text-center">
                    All
                  </Menu.Item>
                  {Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ").map((letter) => (
                    <Menu.Item key={letter} className="text-center">
                      {letter}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <Button
                size="small"
                type="text"
                className="border rounded ms-2 px-2 py-1 bg-light hover-bg-white"
                onClick={(e) => e.stopPropagation()}
                title="Filter by first letter"
              >
                <span className="fw-medium text-primary">
                  {alphaFilter && alphaFilter !== "All" ? alphaFilter : "ALL"}
                </span>
                <DownOutlined
                  className="ms-1 text-muted"
                  style={{ fontSize: 10 }}
                />
              </Button>
            </Dropdown>
          </div>
        ),
        dataIndex: "LeadSource_name",
        key: "LeadSource_name",
        sorter: true,
        render: (_: any, record: LeadSource) => (
          <SubmoduleHoverCard2
            name={highlightMatch(record.LeadSource_name, searchText)}
            role="Lead Source"
            leadsource_name={record.LeadSource_name}
            lead_source_name={record.LeadSource_name}
            createdAt={record.created_at}
            variant2={["contactleadSource", "leadSource"]}

          />
        ),
      },

      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        width: 130,
        align: "center" as const,
        sorter: true,
        render: (value: string) => {
          if (!value) return <span className="text-muted">—</span>;

          const date = new Date(value);
          if (isNaN(date.getTime()))
            return <span className="text-muted">Invalid</span>;

          const formattedDate = date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });

          return <span className="text-muted">{highlightMatch(formattedDate, searchText)}</span>;
        },
      },

      {
        title: (
          <div className="d-flex align-items-center justify-content-between">
            <span className="fw-semibold text-dark">Status</span>

            {/* Status Filter - Professional with counts */}
            <Dropdown
              trigger={["click"]}
              overlay={
                <Menu
                  className="p-2 shadow-sm border-0"
                  style={{ minWidth: 220 }}
                >
                  <Menu.Item
                    key="all"
                    className="d-flex justify-content-between align-items-center py-2 rounded hover-bg-light"
                    onClick={() => setFilterStatuses([])}
                  >
                    <Checkbox checked={filterStatuses.length === 0}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="d-flex align-items-center">
                          All Status
                        </span>
                        <span
                          className="d-flex align-items-center justify-content-end gap-2"
                          style={{ minWidth: 48 }}
                        >
                          <span className="badge bg-light text-dark">
                            {dataSource.length}
                          </span>
                        </span>
                      </div>
                    </Checkbox>
                  </Menu.Item>

                  <Menu.Divider className="my-2" />

                  <Menu.Item
                    key="Active"
                    onClick={() => {
                      setFilterStatuses((prev) =>
                        prev.includes("Active")
                          ? prev.filter((v) => v !== "Active")
                          : [...prev, "Active"]
                      );
                    }}
                  >
                    <Checkbox checked={filterStatuses.includes("Active")}>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="d-flex align-items-center">
                          Active
                        </span>
                        <span
                          className="d-flex align-items-center justify-content-end gap-2"
                          style={{ minWidth: 68 }}
                        >
                          <span className="badge bg-light text-dark">
                            {activeCount}
                          </span>
                        </span>
                      </div>
                    </Checkbox>
                  </Menu.Item>

                  <Menu.Item
                    key="Inactive"
                    onClick={() => {
                      setFilterStatuses((prev) =>
                        prev.includes("Inactive")
                          ? prev.filter((v) => v !== "Inactive")
                          : [...prev, "Inactive"]
                      );
                    }}
                  >
                    <Checkbox checked={filterStatuses.includes("Inactive")}>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="d-flex align-items-center">
                          Inactive
                        </span>
                        <span
                          className="d-flex align-items-center justify-content-end gap-2"
                          style={{ minWidth: 58 }}
                        >
                          <span className="badge bg-light text-dark">
                            {inactiveCount}
                          </span>
                        </span>
                      </div>
                    </Checkbox>
                  </Menu.Item>
                </Menu>
              }
            >
              <Button
                size="small"
                type="text"
                className="ms-2"
                style={{ padding: "0 6px" }}
                onClick={(e) => e.stopPropagation()}
                title="Filter by status"
              >
                <FunnelPlotFilled
                  className={
                    filterStatuses.length > 0 ? "text-primary" : "text-muted"
                  }
                  style={{ fontSize: 16 }}
                />
                {filterStatuses.length > 0 && (
                  <span className="badge bg-primary rounded-pill ms-1 fw-bold">
                    {filterStatuses.length}
                  </span>
                )}
              </Button>
            </Dropdown>
          </div>
        ),
        dataIndex: "Lead_status",
        key: "Lead_status",
        width: 130,
        align: "center" as const,
        sorter: true,
        render: (status: string) => {
          const value = status?.toLowerCase();
          const isActive = value === "active";

          const badgeClass = isActive
            ? "bg-success"
            : value === "inactive"
              ? "bg-danger"
              : "badge-secondary";

          const iconClass = isActive
            ? "ti ti-check"
            : value === "inactive"
              ? "ti ti-x"
              : undefined;

          return (
            <AnimatedBadge
              status={status || "N/A"}
              className={`badge-status ${badgeClass}`}
              icon={iconClass}
            />
          );
        },
      },

      {
        title: <span className="fw-semibold text-dark">Actions</span>,
        key: "action",
        fixed: "right" as const,
        width: 90,
        align: "center" as const,
        render: (_: any, record: LeadSource) => {
          const menu = (
            <Menu className="shadow-lg border-0 p-1">
              <Menu.Item
                key="edit"
                className="d-flex align-items-center gap-2 py-2 px-3 rounded hover-bg-light"
                data-bs-toggle="modal"
                data-bs-target="#edit_calls"
                onClick={() => handleEditClick(record)}
              >
                <i className="ti ti-edit text-primary fs-15 me-2" />
                <span className="fw-medium">Edit</span>
              </Menu.Item>

              <Menu.Item
                key="delete"
                className="d-flex align-items-center gap-2 py-2 px-3 rounded text-danger hover-bg-danger-light"
                data-bs-toggle="modal"
                data-bs-target="#delete_calls"
                onClick={() => setDeleteLeadSourceId(record.LeadSource_id!)}
              >
                <i className="ti ti-trash fs-15 me-2" />
                <span className="fw-medium">Delete</span>
              </Menu.Item>
            </Menu>
          );

          return (
            <Dropdown
              trigger={["click"]}
              overlay={menu}
              placement="bottomRight"
              onOpenChange={(open) =>
                open && setTimeout(() => document.body.click(), 0)
              }
            >
              <Button
                size="small"
                type="text"
                className="table-action-btn"
                onClick={(e) => e.stopPropagation()}
                title="More actions"
              >
                <i className="fa fa-ellipsis-v" />
              </Button>
            </Dropdown>
          );
        },
      },
    ],
    [
      currentPage,
      pageSize,
      searchText,
      alphaFilter,
      filterStatuses,
      dataSource.length,
      activeCount,
      inactiveCount,
      handleEditClick,
      setDeleteLeadSourceId,
    ]
  );

  const handleRowOrderChange = (newOrdered: LeadSource[]) => {
    setOrderedData(newOrdered);
    // OPTIONAL: persist to backend
    // await reorderLeadSources(newOrdered.map(r => r.LeadSource_id));
  };

  // Add after your existing useMemos (totalCount, activeCount, inactiveCount, thisMonthCount)
  const prevMonthCount = useMemo(() => {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    return dataSource.filter((item) => {
      if (!item.created_at) return false;
      const date = new Date(item.created_at);
      return date >= prevMonth && date <= prevMonthEnd;
    }).length;
  }, [dataSource]);

  const thisMonthPercentage = useMemo(() => {
    if (prevMonthCount === 0) return thisMonthCount > 0 ? "100" : "0";
    const diff = ((thisMonthCount - prevMonthCount) / prevMonthCount) * 100;
    return diff.toFixed(1);
  }, [thisMonthCount, prevMonthCount]);

  const activePercentage = useMemo(() => {
    if (totalCount === 0) return "0";
    return ((activeCount / totalCount) * 100).toFixed(1);
  }, [activeCount, totalCount]);

  const inactivePercentage = useMemo(() => {
    if (totalCount === 0) return "0";
    return ((inactiveCount / totalCount) * 100).toFixed(1);
  }, [inactiveCount, totalCount]);
  const handleExportAllPDF = async () => {
    try {
      const blob = await exportLeadSourcePDF();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lead_sources_all_${new Date().toISOString().split("T")[0]
        }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Error toast already handled in API
    }
  };

  const handleExportAllExcel = async () => {
    try {
      const blob = await exportLeadSourceExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lead_sources_all_${new Date().toISOString().split("T")[0]
        }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Error toast already handled
    }
  };

  const handleExportSelectedPDF = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one lead source to export.");
      return;
    }
    try {
      const blob = await exportSelectedLeadSourcePDF(selectedRows);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lead_sources_selected_${selectedRows.length}_${new Date().toISOString().split("T")[0]
        }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Handled in API
    }
  };

  const handleExportSelectedExcel = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one lead source to export.");
      return;
    }
    try {
      const blob = await exportSelectedLeadSourceExcel(selectedRows);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lead_sources_selected_${selectedRows.length}_${new Date().toISOString().split("T")[0]
        }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Handled in API
    }
  };
  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one lead source to delete.");
      return;
    }
    setDeleting(true);
    try {
      await deleteLeadSourceBulk(selectedRows);
      toast.success(
        `${selectedRows.length} Lead Source${selectedRows.length > 1 ? "s" : ""
        } deleted successfully`
      );

      // Refresh data and clear selection
      await loadLeadSources();
      setSelectedRows([]);

      // Close modal
      const modalElement = document.getElementById("bulk_delete_modal");
      if (modalElement) {
        const modalInstance = Modal.getInstance(modalElement);
        modalInstance?.hide();
      }
    } catch (err) {
      // Error already handled in API with toast
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <PageLoadProgressBar />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header mb-0">
                <div className="row align-items-center">
                  <div className="col-sm-8">
                    <h4 className="page-title">
                      <i className="bi bi-database text-primary me-2" />
                      Lead Sources{" "}
                      <span className="count-title ms-2">
                        <CountUp
                          end={dataSource.length}
                          duration={3.0}
                          separator=","
                        />
                      </span>
                    </h4>
                  </div>
                  <div className="col-sm-4 text-sm-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* Breadcrumb */}
              <nav
                aria-label="breadcrumb"
                className="page-header-breadcrumb mb-3"
              >
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link
                      to={
                        userRole === "admin"
                          ? route.dealsDashboard
                          : route.leadsDashboard
                      }
                      style={{
                        color: "#9595b5",
                        textDecoration: "none",
                        transition: "color 0.2s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "#E41F07")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "#9595b5")
                      }
                    >
                      Dashboard
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Lead Source
                  </li>
                </ol>
              </nav>
              {/* /Breadcrumb */}
              <div className="row g-3 mb-4">
                {(() => {
                  const total = totalCount;
                  const activePercent =
                    total > 0 ? ((activeCount / total) * 100).toFixed(1) : 0;
                  const inactivePercent =
                    total > 0 ? ((inactiveCount / total) * 100).toFixed(1) : 0;
                  const thisMonthPercent =
                    total > 0 ? ((thisMonthCount / total) * 100).toFixed(1) : 0;

                  return (
                    <>
                      {/* Total */}
                      <div className="col-lg-3 col-md-6">
                        <Tip
                          text={`Total Lead Sources: ${totalCount} (100%)`}
                          placement="top"
                        >
                          <div className="kpi-card-wrapper">
                            <KpiCard
                              title="Lead Sources"
                              subtitle="Total"
                              value={totalCount}
                              icon="ti ti-users"
                              color="primary"
                              tooltip="Total number of lead sources in the system"
                            />
                          </div>
                        </Tip>
                      </div>

                      {/* This Month */}
                      <div className="col-lg-3 col-md-6">
                        <Tip
                          text={`Added This Month: ${thisMonthCount} (${thisMonthPercent}%)`}
                          placement="top"
                        >
                          <div className="kpi-card-wrapper">
                            <KpiCard
                              title="Lead Sources"
                              subtitle="This Month"
                              value={thisMonthCount}
                              icon="ti ti-calendar"
                              color="warning"
                              tooltip="Lead sources created in the current month"
                              percentage={thisMonthPercentage}
                              percentageColor={
                                parseFloat(thisMonthPercentage) > 0
                                  ? "success"
                                  : "danger"
                              }
                              percentageIcon={
                                parseFloat(thisMonthPercentage) > 0
                                  ? "ti ti-arrow-up"
                                  : "ti ti-arrow-down"
                              }
                            />
                          </div>
                        </Tip>
                      </div>

                      {/* Active */}
                      <div className="col-lg-3 col-md-6">
                        <Tip
                          text={`Active: ${activeCount} (${activePercent}%)`}
                          placement="top"
                        >
                          <div className="kpi-card-wrapper">
                            <KpiCard
                              title="Lead Sources"
                              subtitle="Active"
                              value={activeCount}
                              icon="ti-pulse"
                              color="success"
                              tooltip="Currently active and usable lead sources"
                              percentage={activePercentage}
                              percentageColor="success"
                              percentageIcon="ti ti-arrow-up"
                            />
                          </div>
                        </Tip>
                      </div>

                      {/* Inactive */}
                      <div className="col-lg-3 col-md-6">
                        <Tip
                          text={`Inactive: ${inactiveCount} (${inactivePercent}%)`}
                          placement="top"
                        >
                          <div className="kpi-card-wrapper">
                            <KpiCard
                              title="Lead Sources"
                              subtitle="Inactive"
                              value={inactiveCount}
                              icon="ti-power-off"
                              color="danger"
                              tooltip="Inactive or archived lead sources"
                              percentage={inactivePercentage}
                              percentageColor="danger"
                              percentageIcon="ti ti-arrow-down"
                            />
                          </div>
                        </Tip>
                      </div>
                    </>
                  );
                })()}
              </div>
              {/* /Page Header */}
              <div className="card">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-start">
                    <div className="col-sm-4">
                      <div className="icon-form mb-3 mb-sm-0 position-relative ms-3">
                        <span className="form-icon">
                          <i className="ti ti-search text-muted fs-16" />
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-lg ps-5 rounded-3 border shadow-sm"
                          placeholder="Search Lead Source..."
                          value={searchText}
                          onChange={(e) =>
                            setSearchText(e.target.value.toLowerCase())
                          }
                          style={{ height: "42px", fontSize: "14px" }}
                        />

                        {/* Clear Button - Only shows when there's text */}
                        {searchText && (
                          <button
                            type="button"
                            className="btn-close position-absolute end-0 top-50 translate-middle-y me-3"
                            style={{ zIndex: 10, fontSize: "12px" }}
                            onClick={() => setSearchText("")}
                            aria-label="Clear search"
                          />
                        )}

                        {/* Optional: Live search indicator when typing */}
                        {/* Uncomment if you want a subtle "searching" feel */}
                        {/* {searchText && (
      <span className="position-absolute end-0 top-50 translate-middle-y me-5 text-muted small">
        Searching...
      </span>
    )} */}
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        {/* Import Excel Button */}
                        <button
                          type="button"
                          className="btn btn-outline-success  d-flex align-items-center gap-2 me-2"
                          data-bs-toggle="modal"
                          aria-label="Import lead sources from Excel"
                          data-bs-target="#import_excel_modal"
                        >
                          <i className="ti ti-file-import fs-16" />
                          <span className="d-none d-sm-inline">
                            Import Excel
                          </span>
                        </button>
                        <div className="dropdown me-3">
                          <button
                            type="button"
                            className={`btn btn-outline-secondary d-flex align-items-center gap-2 dropdown-toggle ${selectedRows.length > 0
                              ? "border-primary text-primary"
                              : ""
                              }`}
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            disabled={loading}
                          >
                            <i className="ti ti-package-export fs-16" />
                            <span className="d-none d-sm-inline">Export</span>
                            {selectedRows.length > 0 && (
                              <span className="badge bg-primary rounded-pill ms-2">
                                {selectedRows.length}
                              </span>
                            )}
                          </button>

                          <ul
                            className="dropdown-menu dropdown-menu-end shadow-lg border-0 py-3 px-2"
                            style={{ minWidth: "260px" }}
                          >
                            {/* Header */}
                            <li>
                              <div className="px-3 mb-2">
                                <small className="text-uppercase text-muted fw-bold">
                                  Export Options
                                </small>
                              </div>
                            </li>

                            {/* Export All */}
                            <li>
                              <hr className="dropdown-divider my-1" />
                            </li>
                            <li>
                              <div className="px-3 py-1">
                                <small className="text-muted">
                                  All Records ({filteredData.length})
                                </small>
                              </div>
                            </li>
                            <li>
                              <button
                                className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                                onClick={handleExportAllPDF}
                              >
                                <div className="avatar avatar-sm bg-danger-light rounded">
                                  <i className="ti ti-file-type-pdf text-danger fs-14" />
                                </div>
                                <div className="text-start">
                                  <div className="fw-medium">Export as PDF</div>
                                  <small className="text-muted">
                                    Portable Document Format
                                  </small>
                                </div>
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                                onClick={handleExportAllExcel}
                              >
                                <div className="avatar avatar-sm bg-success-light rounded">
                                  <i className="ti ti-file-spreadsheet text-success fs-14" />
                                </div>
                                <div className="text-start">
                                  <div className="fw-medium">
                                    Export as Excel
                                  </div>
                                  <small className="text-muted">
                                    Spreadsheet (.xlsx)
                                  </small>
                                </div>
                              </button>
                            </li>

                            {/* Export Selected - Only if rows selected */}
                            {selectedRows.length > 0 && (
                              <>
                                <li>
                                  <hr className="dropdown-divider my-2" />
                                </li>
                                <li>
                                  <div className="px-3 py-1">
                                    <small className="text-muted">
                                      Selected Only ({selectedRows.length})
                                    </small>
                                  </div>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                                    onClick={handleExportSelectedPDF}
                                  >
                                    <div className="avatar avatar-sm bg-danger-light rounded">
                                      <i className="ti ti-file-type-pdf text-danger fs-14" />
                                    </div>
                                    <div className="text-start">
                                      <div className="fw-medium">
                                        Export Selected as PDF
                                      </div>
                                    </div>
                                  </button>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                                    onClick={handleExportSelectedExcel}
                                  >
                                    <div className="avatar avatar-sm bg-success-light rounded">
                                      <i className="ti ti-file-spreadsheet text-success fs-14" />
                                    </div>
                                    <div className="text-start">
                                      <div className="fw-medium">
                                        Export Selected as Excel
                                      </div>
                                    </div>
                                  </button>
                                </li>
                              </>
                            )}

                            {/* No selection message */}
                            {selectedRows.length === 0 && (
                              <li>
                                <div className="text-center py-4 px-3">
                                  <i className="ti ti-checkbox fs-32 text-muted d-block mb-2" />
                                  <small className="text-muted">
                                    Select rows to export specific records
                                  </small>
                                </div>
                              </li>
                            )}
                          </ul>
                        </div>
                        <Link
                          to="#"
                          className="btn btn-primary me-3"
                          data-bs-toggle="modal"
                          data-bs-target="#add_calls"
                          style={{
                            position: "relative",
                            overflow: "hidden",
                          }}
                        >
                          <i
                            className="ti ti-square-rounded-plus me-2"
                            style={{ position: "relative", zIndex: 2 }}
                          />
                          <span style={{ position: "relative", zIndex: 2 }}>
                            Add New Lead Source
                          </span>
                          <span
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              background:
                                "linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%)",
                              transform: "skewX(-20deg) translateX(-150%)",
                              animation: "shine 3s infinite",
                            }}
                          />
                        </Link>
                      </div>
                    </div>
                  </div>
                  {/* /Search */}
                </div>
                <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap row-gap-2">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <div className="dropdown me-2">
                      <Link
                        to="#"
                        className="dropdown-toggle dropdown-active-toggle common-sort-dropdown-toggle"
                        data-bs-toggle="dropdown"
                      >


                        <i className="ti ti-sort-ascending-2 me-2" />
                        Sort
                      </Link>
                      <div className="dropdown-menu dropdown-menu-start">
                        <ul className="list-unstyled mb-0">
                          {[
                            { label: "Ascending", value: "ascending" },
                            { label: "Descending", value: "descending" },
                            {
                              label: "Recently Added",
                              value: "recently_added",
                            },
                            // {
                            //   label: "Recently Viewed",
                            //   value: "recently_viewed",
                            // },
                          ].map(({ label, value }) => {
                            const isActive = sortOption === value;
                            return (
                              <li key={value}>
                                <Link
                                  to="#"
                                  className={`dropdown-item d-flex justify-content-between align-items-center ${isActive ? "fw-bold text-primary" : ""
                                    }`}
                                  onClick={() => setSortOption(value)}
                                >
                                  <span>
                                    <i className="ti ti-circle-chevron-right me-1" />
                                    {label}
                                  </span>
                                  {isActive && <i className="ti ti-check" />}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>

                    {/* Date Range Picker */}
                    <div className="icon-form">
                      <span className="form-icon">
                        <i className="ti ti-calendar" />
                      </span>
                      <DateRangePicker
                        initialSettings={initialSettings}
                        onCallback={(start, end, label) => {
                          setSelectedDateRange([start.toDate(), end.toDate()]);
                          if (label) setActiveRangeLabel(label);
                        }}
                      >
                        <input
                          className="form-control bookingrange"
                          type="text"
                          readOnly
                          style={{ minWidth: "200px" }}
                        />
                      </DateRangePicker>
                    </div>


                    <div className="dropdown ms-2">
                      <Link
                        to="#"
                        className="dropdown-toggle d-flex align-items-center gap-2 fw-medium rounded border bg-white"
                        data-bs-toggle="dropdown"
                      >
                        <i className="ti ti-filter-check fs-15 text-muted" />
                        <span>Status</span>
                        <span
                          className="text-capitalize ms-auto fw-semibold status-value-text"
                          style={{ fontSize: "13px" }}
                        >

                          {statusFilter === "all"
                            ? "All"
                            : statusFilter === "active"
                              ? "Active"
                              : "Inactive"}
                        </span>
                      </Link>

                      <div className="dropdown-menu dropdown-menu-start shadow-sm border-0 py-1">
                        <ul className="list-unstyled mb-0">
                          {[
                            {
                              label: "All",
                              value: "all",
                              icon: "ti ti-list-details",
                              count: dataSource.length,
                            },
                            {
                              label: "Active",
                              value: "active",
                              icon: "ti ti-check",
                              color: "text-success",
                              count: activeCount,
                            },
                            {
                              label: "Inactive",
                              value: "inactive",
                              icon: "ti ti-x",
                              color: "text-danger",
                              count: inactiveCount,
                            },
                          ].map(({ label, value, icon, color = "", count }) => {
                            const isActive = statusFilter === value;

                            return (
                              <li key={value}>
                                <Link
                                  to="#"
                                  className={`dropdown-item status-dropdown-item d-flex justify-content-between align-items-center gap-2 py-2 px-3 rounded ${isActive
                                    ? "active fw-semibold" // Changed bg-primary to active class
                                    : "text-dark hover-bg-light"
                                    }`}
                                  onClick={() => setStatusFilter(value)}
                                  style={{ fontSize: "14px" }}
                                >
                                  <span className="d-flex align-items-center gap-2">
                                    <i
                                      className={`ti ${icon} fs-14 ${color} ${isActive ? "text-white" : ""
                                        }`}
                                    />
                                    {label}
                                  </span>

                                  <span className="d-flex align-items-center gap-2">
                                    <span
                                      className={`badge rounded-pill px-2 py-1 fw-medium text-xs ${isActive
                                        ? "bg-white text-dark" // Changed text-primary to text-dark for contrast on active bg
                                        : value === "all"
                                          ? "bg-secondary text-white"
                                          : value === "active"
                                            ? "bg-success text-white"
                                            : "bg-danger text-white"
                                        }`}
                                      
                                    >

                                      {count}
                                    </span>
                                    {isActive && (
                                      <i className="ti ti-check fs-13" />
                                    )}
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {selectedRows.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger d-flex align-items-center gap-2 me-2 ms-2"
                        data-bs-toggle="modal"
                        data-bs-target="#bulk_delete_modal"
                      >
                        <i className="ti ti-trash fs-16" />
                        <span className="d-none d-sm-inline">
                          Delete Selected
                        </span>
                        <span className="badge bg-danger rounded-pill ms-2">
                          {selectedRows.length}
                        </span>
                      </button>
                    )}
                    <div className="d-flex align-items-center ms-2 p-1 border bg-white rounded" style={{ height: "35px" }}>
                      <button
                        className={`btn btn-icon btn-sm view-mode-btn rounded ${viewMode === 'list' ? 'active' : 'text-muted'}`}
                        onClick={() => setViewMode('list')}
                        title="List View"
                        style={{ width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <i className="ti ti-list-details" />
                      </button>
                      <button
                        className={`btn btn-icon btn-sm view-mode-btn rounded ms-1 ${viewMode === 'grid' ? 'active' : 'text-muted'}`}
                        onClick={() => setViewMode('grid')}
                        title="Grid View"
                        style={{ width: "28px", height: "28px", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <i className="ti ti-layout-grid" />
                      </button>
                    </div>
                    {viewMode === 'list' && (
                      <div className="dropdown me-2 ms-2">
                        <Link
                          to="#"
                          className="btn bg-soft-purple text-purple"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                        >
                          <i className="ti ti-columns-3 me-2" />
                          Manage Columns
                        </Link>
                        <div
                          className="dropdown-menu dropdown-menu-end dropdown-md p-3"
                          style={{ minWidth: "250px" }}
                        >
                          <h4 className="mb-2 fw-semibold">Manage Columns</h4>
                          <p className="mb-3">
                            Drag and drop columns to reorder and toggle
                            visibility.
                          </p>
                          <div className="border-top pt-3">
                            <ColumnManagement
                              columnKeys={columnKeys}
                              visibleColumns={visibleColumns}
                              onColumnOrderChange={(newOrder) => setColumnKeys(newOrder)}
                              onColumnToggle={handleColumnToggle}
                              fixedKeys={["action"]} // Keeping Action fixed at the end or wherever it is
                            />
                          </div>
                        </div>
                      </div>
                    )}

                      <div className="form-sorts dropdown">
                        <Link
                          to="#"
                          className="dropdown-toggle dropdown-active-toggle common-filter-btn d-flex align-items-center gap-2 fw-medium"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                        >


                        <i className="ti ti-filter-share" /> Filter
                        {(filterNames.length > 0 ||
                          filterStatuses.length > 0 ||
                          filterCreatedAts.length > 0) && (
                            <span className="badge filter-btn-badge rounded-pill ms-2">
                              {filterNames.length +
                                filterStatuses.length +
                                filterCreatedAts.length}
                            </span>
                          )}
                      </Link>

                      <div className="filter-dropdown-menu dropdown-menu dropdown-menu-md-end p-3">
                        <div className="filter-set-view">
                          <div className="filter-set-head">
                            <h4>
                              <i className="ti ti-filter-share" /> Filter
                            </h4>
                          </div>

                          <div className="accordion" id="accordionExample">
                            {/* 🔹 Lead Source Name Filter */}
                            <div className="filter-set-content">
                              <div className="filter-set-content-head d-flex align-items-center gap-2">
                                <Link
                                  to="#"
                                  className={`filter-accordion-link ${
                                    openAccordion === "leadSourceName"
                                      ? ""
                                      : "collapsed"
                                  }`}
                                  onClick={() =>
                                    handleAccordionToggle("leadSourceName")
                                  }
                                >
                                  Lead Source Name
                                </Link>
                                {filterNames.length > 0 && (
                                  <span className="badge filter-btn-badge rounded-pill small">
                                    {filterNames.length}
                                  </span>
                                )}
                              </div>
                              {openAccordion === "leadSourceName" && (
                                <div className="filter-set-contents accordion-collapse collapse show">
                                  <div className="filter-content-list">
                                    <div className="mb-2 icon-form">
                                      <span className="form-icon">
                                        <i className="ti ti-search" />
                                      </span>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search Lead Source"
                                        value={filterNamesSearch}
                                        onChange={(e) =>
                                          setFilterNamesSearch(e.target.value)
                                        }
                                      />
                                    </div>

                                    <ul className="list-unstyled mb-0">
                                      {uniqueNames
                                        .filter((n) =>
                                          n
                                            .toLowerCase()
                                            .includes(
                                              filterNamesSearch.toLowerCase()
                                            )
                                        )
                                        .map((name) => (
                                          <li key={name}>
                                            <div className="filter-checks">
                                              <label className="checkboxs">
                                                <input
                                                  type="checkbox"
                                                  checked={filterNames.includes(
                                                    name
                                                  )}
                                                  onChange={() =>
                                                    handleFilterChange(
                                                      name,
                                                      filterNames,
                                                      setFilterNames
                                                    )
                                                  }
                                                />
                                                <span className="checkmarks" />{" "}
                                                {name}
                                              </label>
                                            </div>
                                          </li>
                                        ))}

                                      {uniqueNames.filter((n) =>
                                        n
                                          .toLowerCase()
                                          .includes(
                                            filterNamesSearch.toLowerCase()
                                          )
                                      ).length === 0 && (
                                          <p className="text-muted px-2">
                                            No data found
                                          </p>
                                        )}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>

                            <hr className="my-2" />

                            {/* 🔹 Status Filter */}
                            <div className="filter-set-content">
                              <div className="filter-set-content-head d-flex align-items-center gap-2">
                                <Link
                                  to="#"
                                  className={`filter-accordion-link ${
                                    openAccordion === "status"
                                      ? ""
                                      : "collapsed"
                                  }`}
                                  onClick={() =>
                                    handleAccordionToggle("status")
                                  }
                                >
                                  Status
                                </Link>
                                {filterStatuses.length > 0 && (
                                  <span className="badge filter-btn-badge rounded-pill small">
                                    {filterStatuses.length}
                                  </span>
                                )}
                              </div>
                              {openAccordion === "status" && (
                                <div className="filter-set-contents accordion-collapse">
                                  <div className="filter-content-list">
                                    <div className="mb-2 icon-form">
                                      <span className="form-icon">
                                        <i className="ti ti-search" />
                                      </span>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search Status"
                                        value={filterStatusesSearch}
                                        onChange={(e) =>
                                          setFilterStatusesSearch(
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>

                                    <ul className="list-unstyled mb-0">
                                      {uniqueStatuses
                                        .filter((s) =>
                                          s
                                            .toLowerCase()
                                            .includes(
                                              filterStatusesSearch.toLowerCase()
                                            )
                                        )
                                        .map((status) => (
                                          <li key={status}>
                                            <div className="filter-checks">
                                              <label className="checkboxs">
                                                <input
                                                  type="checkbox"
                                                  checked={filterStatuses.includes(
                                                    status
                                                  )}
                                                  onChange={() =>
                                                    handleFilterChange(
                                                      status,
                                                      filterStatuses,
                                                      setFilterStatuses
                                                    )
                                                  }
                                                />
                                                <span className="checkmarks" />{" "}
                                                {status}
                                              </label>
                                            </div>
                                          </li>
                                        ))}

                                      {uniqueStatuses.filter((s) =>
                                        s
                                          .toLowerCase()
                                          .includes(
                                            filterStatusesSearch.toLowerCase()
                                          )
                                      ).length === 0 && (
                                          <p className="text-muted px-2">
                                            No data found
                                          </p>
                                        )}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>

                            <hr className="my-2" />

                            {/* 🔹 Created At Filter */}
                            <div className="filter-set-content">
                              <div className="filter-set-content-head d-flex align-items-center gap-2">
                                <Link
                                  to="#"
                                  className={
                                    openAccordion === "createdAt"
                                      ? ""
                                      : "collapsed"
                                  }
                                  onClick={() =>
                                    handleAccordionToggle("createdAt")
                                  }
                                >
                                  Created At
                                </Link>
                                {filterCreatedAts.length > 0 && (
                                  <span className="badge bg-primary rounded-pill small">
                                    {filterCreatedAts.length}
                                  </span>
                                )}
                              </div>
                              {openAccordion === "createdAt" && (
                                <div className="filter-set-contents accordion-collapse">
                                  <div className="filter-content-list">
                                    <div className="mb-2 icon-form">
                                      <span className="form-icon">
                                        <i className="ti ti-search" />
                                      </span>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search Created At"
                                        value={createdAtSearch}
                                        onChange={(e) =>
                                          setCreatedAtSearch(e.target.value)
                                        }
                                      />
                                    </div>

                                    <ul className="list-unstyled mb-0">
                                      {uniqueCreatedAts
                                        .filter((n) =>
                                          n
                                            .toLowerCase()
                                            .includes(
                                              createdAtSearch.toLowerCase()
                                            )
                                        )
                                        .map((date) => (
                                          <li key={date}>
                                            <div className="filter-checks">
                                              <label className="checkboxs">
                                                <input
                                                  type="checkbox"
                                                  checked={filterCreatedAts.includes(
                                                    date
                                                  )}
                                                  onChange={() =>
                                                    handleFilterChange(
                                                      date,
                                                      filterCreatedAts,
                                                      setFilterCreatedAts
                                                    )
                                                  }
                                                />
                                                <span className="checkmarks" />{" "}
                                                {date}
                                              </label>
                                            </div>
                                          </li>
                                        ))}

                                      {uniqueCreatedAts.filter((n) =>
                                        n
                                          .toLowerCase()
                                          .includes(
                                            createdAtSearch.toLowerCase()
                                          )
                                      ).length === 0 && (
                                          <p className="text-muted px-2">
                                            No data found
                                          </p>
                                        )}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Reset / Apply Buttons */}
                          <div className="filter-reset-btns mt-3">
                            <div className="row">
                              <div className="col-6">
                                <Link
                                  to="#"
                                  className="btn btn-light w-100"
                                  onClick={() => {
                                    setFilterNames([]);
                                    setFilterStatuses([]);
                                    setFilterCreatedAts([]);
                                    setFilterNamesSearch("");
                                    setFilterStatusesSearch("");
                                    setCreatedAtSearch("");
                                    setOpenAccordion(null);
                                  }}
                                >
                                  Reset
                                </Link>
                              </div>
                              <div className="col-6">
                                <Link
                                  to="#"
                                  className="btn btn-primary w-100"
                                  data-bs-dismiss="dropdown"
                                >
                                  Apply
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* 🔹 ACTIVE FILTER TITLES – MUST BE ABOVE TABLE */}
                {(filterNames.length > 0 ||
                  filterStatuses.length > 0 ||
                  filterCreatedAts.length > 0 ||
                  statusFilter !== "all" ||
                  alphaFilter !== "All" ||
                  selectedDateRange ||
                  searchText.length > 0 || // ✅ ADD
                  selectedRows.length > 0 ||
                  sortOption !== "recently_added") && ( // ✅ ADD
                    <div className="active-filters d-flex flex-wrap gap-2 mb-4 align-items-center-3">
                      {/* Search */}
                      {searchText.length > 0 && (
                        <div className="alert alert-primary py-2 mb-0">
                          <strong>Search:</strong> {searchText}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setSearchText("")}
                          />
                        </div>
                      )}

                      {/* Sort */}
                      {sortOption !== "recently_added" && (
                        <div className="alert alert-secondary py-2 mb-0">
                          <strong>Sort:</strong>{" "}
                          {sortOption
                            .replace(/_/g, " ")
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setSortOption("recently_added")}
                          />
                        </div>
                      )}

                      {filterNames.length > 0 && (
                        <div className="alert alert-primary py-2 mb-0">
                          <strong>Lead Source:</strong> {filterNames.join(", ")}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setFilterNames([])}
                          />
                        </div>
                      )}

                      {filterStatuses.length > 0 && (
                        <div className="alert alert-warning py-2 mb-0">
                          <strong>Status:</strong> {filterStatuses.join(", ")}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setFilterStatuses([])}
                          />
                        </div>
                      )}

                      {statusFilter !== "all" && (
                        <div className="alert alert-info py-2 mb-0">
                          <strong>Status:</strong>{" "}
                          {statusFilter.charAt(0).toUpperCase() +
                            statusFilter.slice(1)}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setStatusFilter("all")}
                          />
                        </div>
                      )}

                      {filterCreatedAts.length > 0 && (
                        <div className="alert alert-secondary py-2 mb-0">
                          <strong>Created At:</strong>{" "}
                          {filterCreatedAts.join(", ")}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setFilterCreatedAts([])}
                          />
                        </div>
                      )}

                      {alphaFilter !== "All" && (
                        <div className="alert alert-success py-2 mb-0">
                          <strong>Title starts with:</strong> {alphaFilter}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setAlphaFilter("All")}
                          />
                        </div>
                      )}

                      {selectedDateRange && (
                        <div className="alert alert-dark py-2 mb-0">
                          <strong>Date:</strong>{" "}
                          {selectedDateRange[0].toLocaleDateString()} –{" "}
                          {selectedDateRange[1].toLocaleDateString()}
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setSelectedDateRange(null)}
                          />
                        </div>
                      )}

                      {selectedRows.length > 0 && (
                        <div className="alert alert-success py-2 mb-0">
                          <span>
                            <strong>{selectedRows.length}</strong> lead source
                            {selectedRows.length > 1 ? "s" : ""} selected
                          </span>
                          <i
                            className="ti ti-x ms-2 cursor-pointer"
                            onClick={() => setSelectedRows([])}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Table */}
                  <div className="table-responsive custom-table">
                    {viewMode === 'grid' ? (
                      <div className="p-3">
                        <style>
                          {`
                            .hover-actions {
                              opacity: 0;
                              transition: opacity 0.2s ease-in-out;
                            }
                            .grid-card:hover .hover-actions {
                              opacity: 1;
                            }
                            .grid-card:hover {
                              transform: translateY(-2px);
                              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
                            }
                          `}
                        </style>
                        <GridView
                          data={filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
                          renderCard={(record: any) => (
                            <div className="col-xxl-3 col-xl-4 col-md-6 mb-4">
                              <div className="card h-100 border-0 shadow-sm transition-all grid-card position-relative overflow-hidden" style={{ borderRadius: '12px' }}>
                                <div className="card-body p-4 d-flex flex-column">
                                  {/* Header */}
                                  <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div className="avatar avatar-lg rounded-circle grid-avatar fw-bold d-flex align-items-center justify-content-center"
                                      style={{
                                        width: '50px',
                                        height: '50px',
                                        fontSize: '1.4rem',
                                        background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)',
                                        color: 'white',
                                        boxShadow: '0 4px 10px rgba(255, 107, 107, 0.3)'
                                      }}>
                                      {record.LeadSource_name?.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="d-flex gap-2 hover-actions">
                                      <button
                                        className="btn btn-sm btn-icon btn-light text-primary shadow-sm rounded-circle"
                                        onClick={() => handleEditClick(record)}
                                        data-bs-toggle="modal"
                                        data-bs-target="#edit_calls"
                                        title="Edit"
                                        style={{ width: '32px', height: '32px' }}
                                      >
                                        <i className="ti ti-edit fs-14" />
                                      </button>
                                      <button
                                        className="btn btn-sm btn-icon btn-light text-danger shadow-sm rounded-circle"
                                        onClick={() => setDeleteLeadSourceId(record.LeadSource_id)}
                                        data-bs-toggle="modal"
                                        data-bs-target="#delete_calls"
                                        title="Delete"
                                        style={{ width: '32px', height: '32px' }}
                                      >
                                        <i className="ti ti-trash fs-14" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Content */}
                                  <h5 className="fw-bold text-dark mb-2 text-truncate" title={record.LeadSource_name}>
                                    {record.LeadSource_name}
                                  </h5>

                                  <div className="mb-3">
                                    <span className={`badge rounded-pill fw-medium ${record.Lead_status === 'Active' ? 'bg-success-light text-success' : 'bg-danger-light text-danger'}`}>
                                      <i className={`ti ti-${record.Lead_status === 'Active' ? 'circle-check' : 'circle-x'} me-1`} />
                                      {record.Lead_status}
                                    </span>
                                  </div>

                                  {/* Footer */}
                                  <div className="mt-auto pt-3 border-top d-flex align-items-center justify-content-between text-muted small">
                                    <div className="d-flex align-items-center">
                                      <i className="ti ti-calendar me-1" />
                                      Created:
                                    </div>
                                    <div className="fw-medium">
                                      {new Date(record.created_at).toLocaleDateString()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        />
                        {/* Grid View Pagination */}
                        <div className="d-flex justify-content-end mt-4">
                          <Pagination
                            current={currentPage}
                            pageSize={pageSize}
                            total={filteredData.length}
                            onChange={(page, size) => {
                              setCurrentPage(page);
                              setPageSize(size);
                            }}
                            showSizeChanger
                            pageSizeOptions={["10", "20", "50", "75", "100"]}
                          />
                        </div>
                      </div>
                    ) : error ? (
                      <div className="text-center text-danger my-4">
                        {error}
                      </div>
                    ) : (
                      <Table
                        columns={columns
                          .sort(
                            (a, b) =>
                              columnKeys.indexOf(a.key as string) -
                              columnKeys.indexOf(b.key as string)
                          )
                          .filter(
                            (col) =>
                              visibleColumns[
                              col.key as keyof typeof visibleColumns
                              ]
                          )}
                        dataSource={filteredData}
                        enableRowSelection={true}
                        rowKey="LeadSource_id"
                        loading={loading}
                        className="custom-table"
                        emptyText="No Lead Source found"
                        emptyIcon="ti ti-database-off"
                        recordLabel="Lead Source"
                        pagination={{
                          current: currentPage,
                          showSizeChanger: true,
                          pageSizeOptions: ["10", "20", "50", "75", "100"],
                          pageSize: pageSize,
                          onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                          },
                        }}
                        rowSelection={{
                          type: "checkbox",
                          selectedRowKeys: selectedRows,
                          onChange: (selectedRowKeys: React.Key[]) => {
                            setSelectedRows(selectedRowKeys as string[]);
                          },
                        }}
                        draggable={true}
                        enableColumnDrag={true}
                        onRowOrderChange={handleRowOrderChange}
                        onColumnOrderChange={(newCols: any[]) => {
                          // Get the new order of non-sticky columns from the dragged columns
                          const newOrderedKeys = newCols
                            .filter((c) => !c.fixed) // Only non-sticky columns
                            .map((c) => c.key || c.dataIndex)
                            .filter(
                              (key) =>
                                key !== undefined && key !== null && key !== ""
                            ); // Remove invalid keys

                          // Preserve columns that are not in newCols (hidden columns) by merging with existing columnKeys
                          const existingKeys = columnKeys;
                          const allNonStickyKeys = defaultColumnOrder; // All possible non-sticky column keys

                          // Create a new array that:
                          // 1. Uses the new order for visible columns
                          // 2. Preserves the order of hidden columns that aren't in newCols
                          const preservedKeys = existingKeys.filter(
                            (key) => !newOrderedKeys.includes(key)
                          );
                          const finalKeys = [
                            ...newOrderedKeys,
                            ...preservedKeys,
                          ];

                          // Ensure all default columns are included (in case some are missing)
                          const missingKeys = allNonStickyKeys.filter(
                            (key) => !finalKeys.includes(key)
                          );
                          const completeKeys = [...finalKeys, ...missingKeys];

                          setColumnKeys(completeKeys);
                          // Don't auto-save, let user decide on navigation/refresh
                        }}
                        scroll={{ x: "max-content" }}
                      />
                    )}
                  </div>
                
                  {/* /Table */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Add New Source */}
      {/* Add New Lead Source Modal - Premium Enhanced */}
      <div
        className="modal fade effect-modal-show"
        id="add_calls"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="addLeadSourceLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-primary fw-bold"
                id="addLeadSourceLabel"
              >
                <i className="ti ti-square-rounded-plus-filled me-2 fs-20" />
                Add New Lead Source
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
              <div className="modal-body">
                {/* Lead Source Name */}
                <div className="mb-3">
                  <label
                    className={`col-form-label fw-semibold ${addNameError ? "pro-shake text-danger" : "text-dark"
                      }`}
                    htmlFor="lead-source-input"
                  >
                    Lead Source Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="lead-source-input"
                    className={`form-control form-control-lg ${addNameError ? "is-invalid border-danger" : ""
                      }`}
                    value={newName}
                    // onChange={(e) => {
                    //   const value = e.target.value;
                    //   setNewName(value);

                    //   const res = validateAddLeadSourceAll(value);

                    //   runValidation(res, setAddNameError);

                    //   // Enable button only if valid
                    //   setIsCreateValid(res.valid);
                    // }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewName(value);

                      const res = validateAddLeadSourceAll(value);
                      const hasError = !res.valid;

                      runValidation(res, setAddNameError);

                      const label = document.querySelector(
                        '#add_calls label[for="lead-source-input"]'
                      ) as HTMLElement;

                      if (hasError) {
                        label?.classList.add("pro-shake");
                      } else {
                        label?.classList.remove("pro-shake");
                      }

                      setIsCreateValid(res.valid);
                    }}
                    placeholder="e.g. Website, Referral, Facebook Ads"
                    autoFocus
                  />
                  {addNameError && (
                    <div className="invalid-feedback fw-medium mt-2">
                      {addNameError}
                    </div>
                  )}
                </div>

                <div className="mb-0">
                  <label className="col-form-label d-block">Status</label>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="add-active"
                        name="add-status"
                        checked={newStatus === "Active"}
                        onChange={() => {
                          setNewStatus("Active");
                          runValidation(
                            validateLeadStatus("Active"),
                            setAddStatusError
                          );
                        }}
                      />
                      <label className="form-check-label" htmlFor="add-active">
                        Active
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="add-inactive"
                        name="add-status"
                        checked={newStatus === "Inactive"}
                        onChange={() => {
                          setNewStatus("Inactive");
                          runValidation(
                            validateLeadStatus("Inactive"),
                            setAddStatusError
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="add-inactive"
                      >
                        Inactive
                      </label>
                    </div>
                  </div>
                  {addStatusError && (
                    <div className="text-danger small mt-2 fw-medium">
                      {addStatusError}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <div className="d-flex align-items-center justify-content-end m-0 w-100">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreateLeadSource}
                    data-bs-dismiss="modal"
                    disabled={!isCreateValid || saving}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-check me-2" />
                        Create Lead Source
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Add New Source */}
      {/* Edit Source*/}
      {/* Edit Lead Source Modal - Exact Match with Your Final Add Modal */}
      <div
        className="modal fade effect-modal-show"
        id="edit_calls"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="editLeadSourceLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-primary fw-bold"
                id="editLeadSourceLabel"
              >
                <i className="ti ti-edit me-2 fs-20" />
                Edit Lead Source
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
              <div className="modal-body">
                {/* Lead Source Name */}
                <div className="mb-3">
                  <label
                    className={`col-form-label fw-semibold ${editNameError ? "pro-shake text-danger" : "text-dark"
                      }`}
                    htmlFor="edit-lead-source-input"
                  >
                    Lead Source Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="edit-lead-source-input"
                    className={`form-control form-control-lg ${editNameError ? "is-invalid border-danger" : ""
                      }`}
                    value={editName}
                    // onChange={(e) => {
                    //   const value = e.target.value;
                    //   setEditName(value);

                    //   const res = validateLeadsource(value);
                    //   const valid = runValidation(res, setEditNameError);

                    //   setIsEditValid(valid); // enable/disable Save button
                    // }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditName(value);

                      const res = validateLeadsource(value);
                      const hasError = !res.valid;

                      runValidation(res, setEditNameError);

                      const label = document.querySelector(
                        '#edit_calls label[for="edit-lead-source-input"]'
                      ) as HTMLElement;

                      if (hasError) {
                        label?.classList.add("pro-shake");
                      } else {
                        label?.classList.remove("pro-shake");
                      }

                      setIsEditValid(
                        res.valid &&
                        (editStatus === "Active" || editStatus === "Inactive")
                      );
                    }}
                    placeholder="e.g. Website, Referral, Facebook Ads"
                    autoFocus
                  />
                  {editNameError && (
                    <div className="invalid-feedback fw-medium mt-2">
                      {editNameError}
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="mb-0">
                  <label className="col-form-label d-block">Status</label>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="edit-active"
                        name="edit-status"
                        checked={editStatus === "Active"}
                        onChange={() => {
                          setEditStatus("Active");
                          runValidation(
                            validateLeadStatus("Active"),
                            setEditStatusError
                          );
                        }}
                      />
                      <label className="form-check-label" htmlFor="edit-active">
                        Active
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="edit-inactive"
                        name="edit-status"
                        checked={editStatus === "Inactive"}
                        onChange={() => {
                          setEditStatus("Inactive");
                          runValidation(
                            validateLeadStatus("Inactive"),
                            setEditStatusError
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="edit-inactive"
                      >
                        Inactive
                      </label>
                    </div>
                  </div>
                  {editStatusError && (
                    <div className="text-danger small mt-2 fw-medium">
                      {editStatusError}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <div className="d-flex align-items-center justify-content-end m-0 w-100">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveEdit}
                    data-bs-dismiss="modal"
                    disabled={!isEditValid || saving}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-check me-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ModalDeleteEffect />
      {/* /Edit Calls */}
      {/* Delete Calls */}
      {/* Bulk Delete Modal - Enhanced */}
      <div
        className="modal fade effect-modal-show"
        id="bulk_delete_modal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="bulkDeleteLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-danger fw-bold"
                id="bulkDeleteLabel"
              >
                <i className="ti ti-alert-triangle me-2" />
                Confirm Bulk Deletion
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body text-center ">
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-4 mx-auto effect-avatar-pulse">
                <i className="ti ti-trash fs-40 text-danger" />
              </div>
              <h4 className="fw-bold text-dark mb-3">
                Delete {selectedRows.length} Lead Source
                {selectedRows.length > 1 ? "s" : ""}?
              </h4>
              <p className="text-muted fs-15 mb-4 px-4">
                This action <strong>cannot be undone</strong>. The selected lead
                sources will be
                <strong> permanently removed</strong> from your system.
              </p>
              <div className="alert alert-warning py-3 mx-4 mb-4 rounded-3 border-warning">
                <i className="ti ti-info-circle me-2" />
                <strong>Warning:</strong> This may affect leads currently using
                these sources.
              </div>
              <div className="d-flex gap-3 justify-content-center  mt-4">
                <button
                  type="button"
                  className="btn btn-light px-5 py-2 fw-medium"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger px-5 py-2 fw-medium"
                  onClick={handleBulkDelete}
                  data-bs-dismiss="modal"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-trash me-2" />
                      Yes, Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Single Delete Modal - Enhanced (Consistent Style) */}
      <div
        className="modal fade effect-modal-show"
        id="delete_calls"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="deleteCallsLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-danger fw-bold"
                id="deleteCallsLabel"
              >
                <i className="ti ti-alert-triangle me-2" />
                Confirm Deletion
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body text-center ">
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-4 mx-auto effect-avatar-pulse">
                <i className="ti ti-trash fs-40 text-danger" />
              </div>
              <h4 className="fw-bold text-dark mb-3">
                Delete This Lead Source?
              </h4>
              <p className="text-muted fs-15 mb-4 px-4">
                This action <strong>cannot be undone</strong>. The lead source
                will be
                <strong> permanently removed</strong>.
              </p>
              <div className="alert alert-warning py-3 mx-4 mb-4 rounded-3 border-warning">
                <i className="ti ti-info-circle me-2" />
                <strong>Warning:</strong> This may affect leads currently linked
                to this source.
              </div>
              <div className="d-flex gap-3 justify-content-center mt-4">
                <button
                  type="button"
                  className="btn btn-light px-5 py-2 fw-medium"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger px-5 py-2 fw-medium"
                  data-bs-dismiss="modal"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-trash me-2" />
                      Yes, Delete It
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Delete Calls */}

      {/* Import Excel Modal */}
      {/* Enhanced Minimal Import Excel Modal with Drag & Drop */}
      <div
        className="modal fade effect-modal-show"
        id="import_excel_modal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="importExcelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-success fw-bold d-flex align-items-center gap-2"
                id="importExcelLabel"
              >
                <i className="ti ti-file-import fs-22" />
                Import Lead Sources
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <div className="modal-body ">
              {!importResult ? (
                <>
                  {/* Drag & Drop Zone */}
                  <div
                    className={`border-2 rounded-3 p-4 text-center transition-all ${importFile
                      ? "border-success bg-success-light"
                      : "border-dashed border-success bg-success-subtle"
                      }`}
                    style={{
                      cursor: "pointer",
                      minHeight: "180px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add(
                        "bg-success-light",
                        "border-success"
                      );
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove(
                        "bg-success-light",
                        "border-success"
                      );
                      if (!importFile)
                        e.currentTarget.classList.add("border-dashed");
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove(
                        "bg-success-light",
                        "border-success"
                      );
                      const file = e.dataTransfer.files[0];
                      if (file && file.name.toLowerCase().endsWith(".xlsx")) {
                        setImportFile(file);
                      } else {
                        toast.error("Please upload a valid .xlsx file");
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept=".xlsx"
                      className="d-none"
                      id="excel-upload"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setImportFile(file);
                      }}
                    />

                    <i
                      className={`ti ti-file-upload fs-40 mb-3 ${importFile ? "text-success" : "text-success"
                        }`}
                    />

                    {importFile ? (
                      <div className="text-center">
                        <p className="fw-bold text-dark mb-1">
                          {importFile.name}
                        </p>
                        <p className="text-muted small mb-3">
                          {importFile.size > 1024 * 1024
                            ? (importFile.size / (1024 * 1024)).toFixed(1) +
                            " MB"
                            : (importFile.size / 1024).toFixed(1) + " KB"}
                        </p>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger rounded-pill px-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImportFile(null);
                          }}
                        >
                          <i className="ti ti-x fs-14 me-1" />
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="fw-semibold text-dark mb-2">
                          Drag & drop Excel file here
                        </p>
                        <p className="text-muted small mb-2">or</p>
                        <label
                          htmlFor="excel-upload"
                          className="btn btn-success rounded-pill px-4 py-2 shadow-sm"
                        >
                          <i className="ti ti-file-upload me-2" />
                          Browse File
                        </label>
                        <p className="text-muted small mt-3 mb-0">
                          Supports <code>.xlsx</code> format only
                        </p>
                      </>
                    )}
                  </div>

                  {/* Template Link */}
                  <div className="text-center mt-3">
                    <a
                      href="/lead_source.xlsx"
                      download="lead_source_template.xlsx"
                      className="text-primary small d-inline-flex align-items-center fw-medium hover-text-primary-dark transition-colors"
                    >
                      <i className="ti ti-file-download me-1" />
                      Download Sample Template (.xlsx)
                    </a>
                  </div>
                </>
              ) : (
                /* Success State */
                <div className="text-center">
                  {/* Bold Green Checkmark Circle */}
                  <div
                    className="mx-auto mb-4 d-inline-flex align-items-center justify-content-center rounded-circle bg-success text-white shadow-lg"
                    style={{ width: "90px", height: "90px" }}
                  >
                    <i className="ti ti-check fs-48" />
                  </div>

                  <h5 className="fw-bold text-success mb-3">
                    Import Successful!
                  </h5>

                  <p className="text-dark fw-semibold mb-2">
                    <strong>{importResult.added.length}</strong> lead source
                    {importResult.added.length !== 1 ? "s" : ""} added
                  </p>

                  {importResult.skipped.length > 0 && (
                    <p className="text-danger fw-medium mb-0 d-flex align-items-center justify-content-center gap-2">
                      <i className="ti ti-alert-triangle fs-16" />
                      <strong>{importResult.skipped.length}</strong> row
                      {importResult.skipped.length !== 1 ? "s" : ""} skipped due
                      to errors
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <div className="d-flex justify-content-end gap-3 w-100">
                {!importResult ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-light px-4 py-2 rounded-3"
                      data-bs-dismiss="modal"
                      onClick={() => setImportFile(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-success px-5 py-2 rounded-3 d-flex align-items-center gap-2 shadow-sm"
                      disabled={!importFile || importing}
                      onClick={async () => {
                        if (!importFile) return;
                        setImporting(true);
                        try {
                          const result = await importLeadSourceExcel(
                            importFile
                          );
                          setImportResult(result);
                          await loadLeadSources();
                        } catch (err) {
                          // toast handled in API
                        } finally {
                          setImporting(false);
                        }
                      }}
                    >
                      {importing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Importing Lead Source...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-table-import" />
                          Import File
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary px-5 py-2 rounded-3 w-100"
                    data-bs-dismiss="modal"
                    onClick={() => {
                      setImportResult(null);
                      setImportFile(null);
                    }}
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Save Column Order Modal */}
      <SaveModal />
      <ScrollToTop />
    </>
  );
};

export default LeadsSource;
